import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import api from '../../../utils/api';
import { toast } from 'react-hot-toast';

const ReportCategoryCard = ({ report, onGenerate, onPreview, filters = {} }) => {
  const { title, description, icon, iconColor, iconBg, status: initialStatus } = report;
  const [status, setStatus] = useState(initialStatus);
  const [generatedReportId, setGeneratedReportId] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  
  const handleGenerate = async () => {
    try {
      setStatus('generating');
      
      // Map report titles to report types and fields
      const reportConfig = {
        'Inventory Valuation': {
          type: 'valuation',
          fields: ['product_name', 'sku', 'barcode', 'category', 'current_stock', 'unit_cost', 'total_value']
        },
        'Movement Analysis': {
          type: 'movement',
          fields: ['product_name', 'barcode', 'date', 'quantity', 'type']
        },
        'Forecasting Reports': {
          type: 'forecasting',
          fields: ['product_name', 'barcode', 'current_stock', 'reorder_point', 'forecast_days_remaining', 'recommended_order']
        }
      };
      
      const config = reportConfig[title] || { type: 'inventory', fields: ['product_name', 'quantity', 'unit_cost'] };
      
      // Generate the report
      const { data } = await api.post('/reports/generate', {
        type: config.type,
        name: title,
        fields: config.fields,
        filters
      });
      
      setGeneratedReportId(data.id);
      setGeneratedAt(new Date());
      setStatus('ready');
      toast.success(`${title} report generated successfully!`);
      
      // Call the onGenerate callback if provided
      if (onGenerate) {
        onGenerate(data);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setStatus('error');
      toast.error(`Failed to generate ${title} report. Please try again.`);
    }
  };
  
  const handlePreview = () => {
    if (onPreview) {
      onPreview({
        ...report,
        id: generatedReportId
      });
    }
  };
  
  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start">
        <div className={`p-3 rounded-lg ${iconBg} mr-4`}>
          <Icon name={icon} size={24} color={iconColor} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-muted-foreground text-sm mt-1">{description}</p>
          
          <div className="flex items-center mt-4 text-xs text-muted-foreground">
            <span className="flex items-center">
              <Icon name="Clock" size={14} className="mr-1" />
              {generatedAt ? `Generated ${formatDistanceToNow(generatedAt)} ago` : 'Ready to generate'}
            </span>
            
            {status === 'generating' ? (
              <span className="ml-4 flex items-center">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-warning opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-warning"></span>
                </span>
                Generating...
              </span>
            ) : status === 'error' ? (
              <span className="ml-4 flex items-center">
                <span className="h-2 w-2 rounded-full bg-destructive mr-2"></span>
                Error
              </span>
            ) : (
              <span className="ml-4 flex items-center">
                <span className="h-2 w-2 rounded-full bg-success mr-2"></span>
                Ready
              </span>
            )}
          </div>
          
          <div className="flex items-center mt-4 space-x-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handlePreview}
              disabled={status === 'generating' || !generatedReportId}
            >
              Preview
            </Button>
            <Button 
              variant="default" 
              size="sm"
              disabled={status === 'generating'}
              onClick={handleGenerate}
            >
              Generate
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCategoryCard;